import { CalendarDays, Check, MessageSquare, Pencil, Search, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import type { ConversacionResumen } from '../asistente.types';
import {
  useBorrarConversacion,
  useConversaciones,
  useRenombrarConversacion,
} from '../hooks/use-asistente-historial';

interface HistorialPanelProps {
  open: boolean;
  onClose: () => void;
  /** Conversación abierta ahora mismo (se resalta en la lista). */
  conversacionId: number | null;
  /** Carga en la pantalla la conversación elegida. */
  onAbrir: (id: number) => void;
  /** Empieza una conversación nueva. */
  onNueva: () => void;
}

/** «2026-10-15 16:40:12» → «15/10/2026 16:40» */
function fechaLegible(valor: string | null): string {
  if (!valor) return '';

  const [dia, hora = ''] = valor.split(' ');

  if (!dia) return '';

  const [anio, mes, d] = dia.split('-');

  return `${d}/${mes}/${anio}${hora ? ` ${hora.slice(0, 5)}` : ''}`;
}

/**
 * Historial de conversaciones del usuario.
 *
 * Cada conversación se guarda en el servidor, así que se puede reabrir desde
 * cualquier equipo. Se avisa de que las cifras son «de cuando se preguntó»:
 * un hilo antiguo muestra el dato de su fecha, no el de hoy.
 */
export function HistorialPanel({
  open,
  onClose,
  conversacionId,
  onAbrir,
  onNueva,
}: HistorialPanelProps) {
  const [buscar, setBuscar] = useState('');
  const [renombrando, setRenombrando] = useState<number | null>(null);
  const [tituloNuevo, setTituloNuevo] = useState('');

  const conversaciones = useConversaciones(buscar, open);
  const renombrar = useRenombrarConversacion();
  const borrar = useBorrarConversacion();

  const lista = conversaciones.data ?? [];

  const guardarTitulo = (id: number) => {
    const titulo = tituloNuevo.trim();

    if (titulo === '') {
      setRenombrando(null);
      return;
    }

    renombrar.mutate({ id, titulo }, { onSuccess: () => setRenombrando(null) });
  };

  const confirmarBorrado = (conversacion: ConversacionResumen) => {
    const aviso = `¿Borrar la conversación «${conversacion.titulo}»? Esta acción no se puede deshacer.`;

    if (window.confirm(aviso)) borrar.mutate(conversacion.id);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Historial de conversaciones"
      description="Se guardan en tu cuenta: puedes reabrirlas desde cualquier equipo. Las cifras que verás son las de la fecha en que se preguntó."
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onNueva();
              onClose();
            }}
          >
            Empezar una conversación nueva
          </Button>

          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400"
            aria-hidden="true"
          />

          <Input
            value={buscar}
            onChange={(evento) => setBuscar(evento.target.value)}
            placeholder="Buscar por título o por lo que se preguntó…"
            aria-label="Buscar en el historial"
            className="pl-9"
          />
        </div>

        {conversaciones.isPending || conversaciones.isFetching ? (
          <p className="py-6 text-center text-sm text-ink-500">Cargando historial…</p>
        ) : null}

        {!conversaciones.isFetching && lista.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-500">
            {buscar.trim() === ''
              ? 'Todavía no tienes conversaciones guardadas.'
              : 'No hay conversaciones que coincidan con la búsqueda.'}
          </p>
        ) : null}

        <ul className="space-y-2">
          {lista.map((conversacion) => {
            const activa = conversacion.id === conversacionId;

            return (
              <li
                key={conversacion.id}
                className={cn(
                  'rounded-xl border px-3 py-2 transition-colors',
                  activa ? 'border-brand-200 bg-brand-50/50' : 'border-ink-100 bg-white hover:border-ink-200',
                )}
              >
                <div className="flex items-start gap-2">
                  <MessageSquare
                    className={cn('mt-0.5 size-4 shrink-0', activa ? 'text-brand-600' : 'text-ink-400')}
                    aria-hidden="true"
                  />

                  <div className="min-w-0 flex-1">
                    {renombrando === conversacion.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input
                          value={tituloNuevo}
                          onChange={(evento) => setTituloNuevo(evento.target.value)}
                          onKeyDown={(evento) => {
                            if (evento.key === 'Enter') guardarTitulo(conversacion.id);
                            if (evento.key === 'Escape') setRenombrando(null);
                          }}
                          aria-label="Nuevo título"
                          autoFocus
                        />

                        <button
                          type="button"
                          onClick={() => guardarTitulo(conversacion.id)}
                          aria-label="Guardar título"
                          className="cursor-pointer rounded-md p-1 text-emerald-600 hover:bg-emerald-50"
                        >
                          <Check className="size-4" />
                        </button>

                        <button
                          type="button"
                          onClick={() => setRenombrando(null)}
                          aria-label="Cancelar"
                          className="cursor-pointer rounded-md p-1 text-ink-500 hover:bg-ink-100"
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          onAbrir(conversacion.id);
                          onClose();
                        }}
                        className="block w-full cursor-pointer text-left"
                      >
                        <p className="truncate text-sm font-medium text-ink-900">{conversacion.titulo}</p>

                        <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-ink-500">
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays className="size-3" aria-hidden="true" />
                            {fechaLegible(conversacion.actualizada_en)}
                          </span>
                          <span>{conversacion.mensajes} mensaje(s)</span>
                        </p>
                      </button>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setRenombrando(conversacion.id);
                        setTituloNuevo(conversacion.titulo);
                      }}
                      aria-label={`Renombrar «${conversacion.titulo}»`}
                      className="cursor-pointer rounded-md p-1.5 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
                    >
                      <Pencil className="size-3.5" />
                    </button>

                    <button
                      type="button"
                      onClick={() => confirmarBorrado(conversacion)}
                      aria-label={`Borrar «${conversacion.titulo}»`}
                      className="cursor-pointer rounded-md p-1.5 text-ink-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Dialog>
  );
}
